import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios';

// Extend axios types to include custom properties
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    metadata?: { startTime: Date };
  }
  
  interface AxiosError {
    apiError?: ApiError;
  }
}

// Environment-aware API URL configuration
const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:5000/api'; //https://grafana.sculpxtechlabs.cloud
    }
  }
  return process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
};

// Unified API response interface
export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
}

// Pagination metadata interface
export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

// API Error interface
export interface ApiError {
  message: string;
  status?: number;
  code?: string;
  details?: any;
}

// Enhanced axios instance with comprehensive configuration
export const api: AxiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Performance monitoring in development
if (process.env.NODE_ENV === 'development') {
  api.interceptors.request.use((config) => {
    config.metadata = { startTime: new Date() };
    return config;
  });

  api.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
}

// Request interceptor for authentication
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for comprehensive error handling
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    const apiError: ApiError = {
      message: 'An unexpected error occurred',
      status: error.response?.status,
    };

    if (error.response) {
      // Server responded with error status
      const responseData = error.response.data as any;
      apiError.message = responseData?.message || responseData?.error || `Server error: ${error.response.status}`;
      apiError.status = error.response.status;
      apiError.code = responseData?.code;
      apiError.details = responseData?.details;

      // Handle authentication errors
      if (error.response.status === 401) {
        const currentPath = window.location.pathname;
        if (currentPath !== '/login' && currentPath !== '/register') {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
      }
    } else if (error.request) {
      // Network error
      apiError.message = 'Network error. Please check your internet connection.';
      apiError.code = 'NETWORK_ERROR';
    } else {
      // Request setup error
      apiError.message = error.message || 'Request configuration error';
      apiError.code = 'REQUEST_ERROR';
    }

    // Attach formatted error to the error object
    error.apiError = apiError;
    return Promise.reject(error);
  }
);

// Helper function to format API errors
export const formatApiError = (error: any): ApiError => {
  if (error.apiError) {
    return error.apiError;
  }
  
  return {
    message: error.message || 'An unexpected error occurred',
    status: error.response?.status,
    code: 'UNKNOWN_ERROR',
  };
};

// Helper function to build query parameters
export const buildQueryParams = (params: Record<string, any>): string => {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach(item => searchParams.append(key, item.toString()));
      } else {
        searchParams.append(key, value.toString());
      }
    }
  });
  
  return searchParams.toString();
};

// Helper function to validate required parameters
export const validateRequiredParams = (params: Record<string, any>, requiredFields: string[]): void => {
  const missingFields = requiredFields.filter(field => 
    params[field] === undefined || params[field] === null || params[field] === ''
  );
  
  if (missingFields.length > 0) {
    throw new Error(`Missing required parameters: ${missingFields.join(', ')}`);
  }
};

export default api;
